import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useSettings } from '../context/SettingsContext';
import API_URL from '../config';

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const { theme } = useSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-puzzle-background flex items-center justify-center p-4">
        <div className="bg-puzzle-surface rounded-2xl shadow-xl w-full max-w-md p-8 border border-puzzle-border text-center">
          <div className="mb-6">
            <svg className="h-16 w-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-puzzle-text mb-4">Check Your Email</h2>
          <p className="text-puzzle-text-secondary mb-6">
            If an account exists for
            {' '}
            <span className="font-medium text-puzzle-text">{email}</span>
            , we&apos;ve sent instructions to reset your password.
          </p>
          <p className="text-puzzle-text-secondary text-sm mb-6">
            The link will expire in 1 hour.
          </p>
          <Link to="/login" className="text-puzzle-blue hover:text-blue-600 font-semibold">
            Return to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-puzzle-background flex items-center justify-center p-4">
      <div className="bg-puzzle-surface rounded-2xl shadow-xl w-full max-w-md p-8 border border-puzzle-border">
        {/* Logo and Branding */}
        <div className="flex flex-col items-center mb-8">
          <img
            src={theme === 'dark' ? '/images/logo_with_text_dark_mode.png' : '/images/logo_with_text.png'}
            alt="Puzzlists"
            className="h-24"
          />
          <h2 className="text-xl font-bold text-puzzle-text mt-4">Forgot Password?</h2>
          <p className="text-puzzle-text-secondary mt-2 text-center">
            Enter your email and we&apos;ll send you a link to reset your password.
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-puzzle-danger/10 border border-puzzle-danger/30 rounded-lg p-4 flex items-center gap-3">
            <svg className="h-5 w-5 text-puzzle-danger flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <p className="text-sm text-puzzle-danger">{error}</p>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="forgot-email" className="block text-sm font-medium text-puzzle-text mb-1">
              Email address
              <input
                id="forgot-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full px-4 py-3 rounded-lg border border-puzzle-border bg-puzzle-surface focus:ring-2 focus:ring-puzzle-blue focus:border-transparent transition-shadow text-puzzle-text placeholder-puzzle-text-secondary font-normal"
                placeholder="you@example.com"
              />
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 px-4 bg-puzzle-blue hover:bg-blue-600 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 disabled:bg-puzzle-blue/60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading && (
              <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {loading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        {/* Footer */}
        <p className="text-center mt-8 text-puzzle-text-secondary">
          Remember your password?
          {' '}
          <Link to="/login" className="font-semibold text-puzzle-blue hover:text-blue-600">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

export default ForgotPassword;
